/**
 * Dry-run of this pass (#2575). Title and banner both say it writes
 * nothing and sends nothing. Emails arrive already masked; this table
 * does not try to restore them.
 */
import type { CodexCapacityAlertPreviewResponse } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Table, TableEmpty, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';

interface Props {
  readonly preview: CodexCapacityAlertPreviewResponse;
}

export function CodexCapacityPreviewPanel({ preview }: Props): JSX.Element {
  const t = useT();
  const decisionKey =
    preview.archive_stale
      ? 'codex_capacity.preview.decision_archive_stale'
      : preview.send_kind === 'elevated' && preview.decision === 'hold'
        ? 'codex_capacity.preview.decision_catch_up'
        : preview.decision === 'fire'
          ? 'codex_capacity.preview.decision_fire'
          : preview.decision === 'recover'
            ? 'codex_capacity.preview.decision_recover'
            : 'codex_capacity.preview.decision_hold';

  return (
    <div className="flex flex-col gap-3">
      <p
        className="m-0 rounded border border-line-soft bg-well px-3 py-2 text-dense text-paper-mid"
        data-testid="codex-capacity-preview-banner"
      >
        {preview.alerts_enabled
          ? t('codex_capacity.preview.banner', {
              decision: t(decisionKey),
              count: String(preview.recipients.length),
            })
          : t('codex_capacity.preview.banner_closed')}
      </p>
      <p className="m-0 text-dense text-paper-low" data-testid="codex-capacity-preview-masked-note">
        {t('codex_capacity.preview.masked_note')}
      </p>
      {preview.suppressed > 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="codex-capacity-preview-suppressed">
          {t('codex_capacity.preview.suppressed', { count: String(preview.suppressed) })}
        </p>
      ) : null}

      <Table testId="codex-capacity-preview-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('codex_capacity.preview.column.account')}</Th>
            <Th>{t('codex_capacity.preview.column.email')}</Th>
          </Tr>
        </THead>
        <TBody>
          {preview.recipients.length === 0 ? (
            <TableEmpty colSpan={2}>{t('codex_capacity.preview.empty')}</TableEmpty>
          ) : (
            preview.recipients.map((row) => (
              <Tr
                key={row.account_id}
                interactive={false}
                data-testid={`codex-capacity-preview-row-${row.account_id}`}
              >
                <Td mono>{row.account_id}</Td>
                <Td>{row.email_masked}</Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>
    </div>
  );
}
